import { Box, Typography, Grid, CircularProgress, Divider, Card } from '@mui/material';
import { Formik, Form } from 'formik';
import axios from 'axios';
import React, { useState, useEffect } from "react";
import CustomCardView from '../../common/CustomCardView';
import { READ_PROFORMA_INVOICE } from '../../../services/apiRoutes';
import { IconButton } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import DetailsParticulars from '../../common/DetailsPage/DetailsParticulars';
import { convertNumberToWords, printFunction } from '../../../services/apiServices';
import CustomPrintModel from '../../common/CustomPrint';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { getRoundAmount } from '../../../services/CommonServices';
import { useCompanyDateFormat } from "../../../constants/useCompanyDateFormat";
import { t } from '../../../constants/translationHelper';


const ProformaInvoiceView = ({ handleClose, proformaInvoiceId }) => {
    const companyDateFormat = useCompanyDateFormat();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataParticular, setDataParticular] = useState(null);
    const [totalQty, setTotalQty] = useState(0);
    const [totalRate, setTotalRate] = useState(0);
    const [amountData, setAmount] = useState(0);
    const [amountText, setAmountText] = useState(0);

    const number = amountText || '';
    const amountInWords = convertNumberToWords(number);
    const [currencyName, setCurrencyName] = useState('');

    // Fetch data for the proforma invoice
    useEffect(() => {
        if (!proformaInvoiceId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_PROFORMA_INVOICE(proformaInvoiceId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                const invoiceData = response.data.proformaInvoice;
                const symbol = response.data.proformaInvoice?.currency?.currency_symbol || '';
                const symbolName = response.data.proformaInvoice?.currency?.currency_name || '';
                const particulars = invoiceData.particulars.map(particular => ({
                    qty: particular.qty || 0,
                    rate: `${symbol || ''} ${parseFloat(particular?.rate || 0).toFixed(2)}`,
                    amounts: `${symbol || ''} ${parseFloat(particular?.amount || 0).toFixed(2)}`,
                    amount: (particular.qty || 0) * (parseFloat(particular.rate) || 0),
                    productName: particular.products.productName,
                    unitName: particular.productsUnit.unitName,
                }));
                const amountTotal = particulars.reduce((acc, item) => acc + item.amount, 0);
                const roundedAmount = getRoundAmount(amountTotal);
                setAmount(`${symbol} ${roundedAmount.toFixed(2)}`);
                setAmountText(roundedAmount);
                setData(invoiceData);
                setDataParticular(particulars);
                const totalQuantity = invoiceData.particulars
                    .reduce((total, particular) => total + (parseFloat(particular.qty) || 0), 0)
                setTotalQty(totalQuantity);

                // Calculate total rate
                const totalRateAmount = invoiceData.particulars.reduce((acc, item) => acc + parseFloat(item.rate), 0);
                setTotalRate(totalRateAmount);
                setCurrencyName(symbolName);

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [proformaInvoiceId]);

    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const getLastSuffix = (url) => {
        if (url) {
            const parts = url.split('/');
            return parts[parts.length - 1];
        }
        return '';
    };
    const isLC = data?.paymentMethod?.paymentMethodId === 2;
    // Define labels as an array of objects for all keys
    const labels = [
        { key: 'clientCompanyName', label: "Supplier", sm: 12, md: 4, },
        { key: 'dueDate', label: "Due Date", sm: 12, md: 4, },
        { key: 'proformaInvoiceNo', label: "Proforma Invoice No", sm: 12, md: 4, },
        { key: 'reference', label: "Reference", sm: 12, md: 4, },
        { key: 'date', label: "Date", sm: 12, md: 4, },
        { key: 'currency', label: "currency", sm: 12, md: 4, },
        { key: 'paymentMethodName', label: "payment Type", sm: 12, md: 4, },
        { key: 'advance', label: "advance", sm: 12, md: 4, },
        { key: 'final', label: isLC ? "Against LC" : "final", sm: 12, md: 4 },
        { key: 'paymentAgainst', label: "Against", sm: 12, md: 4, },
        { key: 'creditDay', label: isLC ? "LC credit Day" : "credit Day", sm: 12, md: 4, },
        { key: 'portOfLoading', label: 'port Of Loading', sm: 12, md: 4, },
        { key: 'portOfDestination', label: 'port Of Destination', sm: 12, md: 4, },

    ];



    const values = {
        clientCompanyName: data?.clientCompany?.clientCompanyName || '',
        proformaInvoiceNo: data?.proformaInvoiceNo || '',
        reference: data?.reference || '',
        date: companyDateFormat(data?.date) || '',
        dueDate: companyDateFormat(data?.dueDate) || '',
        note: data?.note || '',
        paymentMethodName: data?.paymentMethod?.paymentMethodName || '',
        paymentAgainst: data?.paymentAgainst?.title || '',
        paymentType: data?.paymentType || '',
        advance: data?.advance ? `${data.advance}%` : '',
        final: data?.final ? `${data.final}%` : '',
        selectAagainst: data?.selectAagainst || '',
        creditDay: data?.creditDay || '',
        piUpload: getLastSuffix(data?.piUpload || ''),
        piUploadurl: data?.piUpload || '',
        currency: data?.currency?.currency || '',
        portOfLoading: `${data?.loading?.portName || ""}, ${data?.loading?.country?.name || ""}`.trim(),
        portOfDestination: `${data?.destination?.portName || ""}, ${data?.destination?.country?.name || ""}`.trim(),
        particulars: data?.particulars || []
    };

    const particularsColumns = [

        {
            id: 'productName',
            label: 'Product Name',
            accessor: 'productName',
        },
        {
            id: 'qty',
            label: 'Quantity',
            accessor: 'qty',
            styleAccessor: {
                textAlign: 'center',
            },
        },
        {
            id: 'rate',
            label: 'Rate',
            accessor: 'rate',
            styleAccessor: {
                textAlign: 'right',
            },
        },
        {
            id: 'unitName',
            label: 'Unit',
            accessor: 'unitName',
            styleAccessor: {
                textAlign: 'center',
            },
        },
        {
            id: 'amounts',
            label: 'Amount',
            accessor: 'amounts',
            styleAccessor: {
                textAlign: 'right',
            },
        },


    ];



    const footerData = [
        {
            label: 'Total',
            footerValue: '',
            style: {
                textAlign: 'right',
            },
            colSpan: 1,

        },
        {
            label: '',
            footerValue: totalQty,
            style: {
                textAlign: 'center',

            },

        },
        {
            label: '',
            footerValue: amountData,
            style: {
                textAlign: 'right',

            },
            colSpan: 3,

        },
    ];
    const printContent = () => {
        const elementId = 'contentId';
        const iframeId = 'print-iframe';
        printFunction(elementId, iframeId);
    };



    return (
        <>
            <Box sx={{ mx: 'auto', mt: 0 }}>
                <Formik
                    initialValues={values}
                    enableReinitialize
                >
                    {() => (
                        <Form>
                            <Grid container spacing={1} style={{ position: "sticky" }}>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
                                        {t("Proforma Invoice")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Box>
                                        <IconButton aria-label="delete">
                                            <DeleteOutlineOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="print" onClick={printContent}>
                                            <LocalPrintshopOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="print" onClick={printContent}>
                                            <PictureAsPdfOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="close" onClick={handleClose}>
                                            <CloseIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Divider />
                            <Grid container spacing={0} sx={{ px: "0%" }}>
                                <Grid item xs={12}>
                                    <CustomCardView title="Bill To" label={labels} data={values} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display="flex" flexDirection="row" alignItems="center">
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                            {t("Particulars")}
                                        </Typography>
                                    </Box>
                                    <DetailsParticulars
                                        title={'Particulars'}
                                        columns={particularsColumns}
                                        data={dataParticular}
                                        footerData={footerData}
                                    />
                                </Grid>
                                <Grid item xs={7}>
                                    <br />
                                    <Box>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("In Words")}
                                        </Typography>
                                        <Typography style={{ color: "#212529", fontWeight: 400, fontSize: "14px", textTransform: "uppercase" }}>
                                            {currencyName} {amountInWords}
                                        </Typography>
                                    </Box>
                                    <br />
                                    <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("PI Document")} :
                                        </Typography>
                                        <a href={values.piUploadurl} target="_blank" rel="noopener noreferrer" style={{ color: "#007bff", textDecoration: "none" }}>
                                            {values.piUpload}
                                        </a>
                                    </Box>

                                    <Box sx={{ pt: 5 }}>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("Note")} :
                                        </Typography>
                                        <Typography style={{ color: "#212529", fontWeight: 400, fontSize: "14px", textTransform: "capitalize" }}>
                                            {values.note}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={5}>
                                    <br />
                                    <Box display="flex" flexDirection="row" alignItems="center">
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                            {t("Summary")}
                                        </Typography>
                                    </Box>
                                    <Card sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 400 }} variant="outlined">
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography>{t("Total Quantity")}:</Typography>
                                            <Typography>{totalQty}</Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography>{t("Amount")}:</Typography>
                                            <Typography> {amountData}</Typography>
                                        </Box>

                                        <Divider sx={{ my: 0 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography fontWeight="bold">{t("Grand Total")}:</Typography>
                                            <Typography fontWeight="bold">{amountData}</Typography>
                                        </Box>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Box>

            <CustomPrintModel contentId="contentId">
                <Typography style={{ textAlign: "center", color: "red" }}>Proforma Invoice </Typography>
            </CustomPrintModel >
        </>

    );
};

export default ProformaInvoiceView;
